import { type ReactNode, createContext, useContext, useMemo } from 'react'

import {
  getArtworkImageSelection,
  getArtworkSizeSelection,
  getFrameStyleSelection,
  getGlassSelection,
  getPassepartoutSelection,
  getSpacerSelection,
} from '@frame-builder/lib/state-selection-messages'
import { type SelectionMessages } from '@frame-builder/lib/types'
import {
  FrameBuilderCommittedStateContext,
  FrameBuilderSelectedStateContext,
} from './state-context'
import { FrameBuilderStringsContext } from './strings-context'

interface FrameBuilderSelectionContextProps {
  committedStateSelectionMessages: SelectionMessages
  selectedStateSelectionMessages: SelectionMessages
}

interface SelectionProviderProps {
  children: ReactNode
}

export const FrameBuilderSelectionContext =
  createContext<FrameBuilderSelectionContextProps>({
    committedStateSelectionMessages: {},
    selectedStateSelectionMessages: {},
  })

export const FrameBuilderSelectionProvider = ({
  children,
}: SelectionProviderProps) => {
  const { committedState } = useContext(FrameBuilderCommittedStateContext)
  const { selectedState } = useContext(FrameBuilderSelectedStateContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)

  const committedStateSelectionMessages = useMemo(() => {
    const artworkSizeSelection = getArtworkSizeSelection(
      committedState,
      frameBuilderStrings,
    )
    const artworkImageSelection = getArtworkImageSelection(committedState)
    const frameStyleSelection = getFrameStyleSelection(committedState)
    const passepartoutSelection = getPassepartoutSelection(committedState)
    const glassSelection = getGlassSelection(committedState)
    const spacerSelection = getSpacerSelection(committedState)

    const selectionMessages: SelectionMessages = {
      ...artworkSizeSelection,
      ...artworkImageSelection,
      ...frameStyleSelection,
      ...passepartoutSelection,
      ...glassSelection,
      ...spacerSelection,
    }

    return selectionMessages
  }, [committedState, frameBuilderStrings])

  const selectedStateSelectionMessages = useMemo(() => {
    const artworkSizeSelection = getArtworkSizeSelection(
      selectedState,
      frameBuilderStrings,
    )
    const artworkImageSelection = getArtworkImageSelection(selectedState)

    const selectionMessages: SelectionMessages = {
      ...artworkSizeSelection,
      ...artworkImageSelection,
    }

    return selectionMessages
  }, [frameBuilderStrings, selectedState])

  return (
    <FrameBuilderSelectionContext.Provider
      value={{
        committedStateSelectionMessages,
        selectedStateSelectionMessages,
      }}
    >
      {children}
    </FrameBuilderSelectionContext.Provider>
  )
}
